/// <amd-module name="TFS/TeamProfilePanel/TeamProfilePanelService" />

import { IProjectData, ITfsPageService } from "Tfs/Platform/Page";
import { ICoreRestClient } from "Tfs/Platform/RestClient/Core";
import * as Resources from "TFS/TeamProfilePanel/Resources";
import { IObservableArray, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { TeamMember } from "VSS/Platform/Contracts/WebApi";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { getIdentityImageUrl } from "VSS/Platform/Identity";
import { routeUrl } from "VSS/Platform/Location";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { ITeamPanelContext, ITeamPanelDataProviderResponse, ITeamPanelItem, ITeamPanelItemView } from "./Models";
import { Types } from "./TeamProfilePanelConstants";

export namespace teamPanelDataProviders {
    export const teamPanelItemsBoardsDataProvider = "ms.vss-tfs-web.team-panel-items-agile-boards-data-provider";
    export const teamPanelItemsBacklogsDataProvider = "ms.vss-tfs-web.team-panel-items-agile-backlogs-data-provider";
    export const teamPanelItemsSprintsDataProvider = "ms.vss-tfs-web.team-panel-items-agile-sprints-data-provider";
    export const teamPanelItemsQueriesDataProvider = "ms.vss-tfs-web.team-panel-items-agile-queries-data-provider";
    export const teamPanelItemsDashboardsDataProvider = "ms.vss-tfs-web.team-panel-items-agile-dashboards-data-provider";
    export const teamPanelMembersPivotDataProvider = "ms.vss-admin-web.org-admin-members-data-provider";
}

export interface ITeamProfilePanelService extends IVssService {
    getDropdownItems(): IObservableArray<string>;
    getHubSelection(): DropdownSelection;
    getIdentityImageUrl(teamId: string): string;
    getIsLoadingItems(): ObservableValue<boolean>;
    getIsLoadingMembers(): ObservableValue<boolean>;
    getItemProvider(teamId: string, teamName: string): IObservableArray<ITeamPanelItemView>;
    getMemberProvider(teamId: string): IObservableArray<TeamMember>;
    getSettingsUrl(teamId: string): string;
    getTeamProject(): IProjectData | undefined;
}

export class TeamProfilePanelService extends VssService implements ITeamProfilePanelService {
    private allItems: ITeamPanelItemView[] = [];
    private dropdownItems: IObservableArray<string> = new ObservableArray<string>([Resources.AllItems]);
    private filteredItems: IObservableArray<ITeamPanelItemView> = new ObservableArray<ITeamPanelItemView>([]);
    private hubSelection = new DropdownSelection();
    private isLoadingItems: ObservableValue<boolean> = new ObservableValue(false);
    private isLoadingMembers: ObservableValue<boolean> = new ObservableValue(false);
    private membersPromise: Promise<void>;
    private members: IObservableArray<TeamMember> = new ObservableArray<TeamMember>([]);
    private maxMembers: 200;
    private teamId: string;
    private teamName: string;

    public _serviceStart = (pageContext: IVssPageContext): void => {
        super._serviceStart(pageContext);
        this.hubSelection.subscribe(this.filterAllItems);
    };

    public _serviceEnd = (pageContext: IVssPageContext): void => {
        super._serviceEnd(pageContext);
        this.hubSelection.unsubscribe(this.filterAllItems);
    };

    public getDropdownItems = (): IObservableArray<string> => {
        return this.dropdownItems;
    };

    public getHubSelection = (): DropdownSelection => {
        return this.hubSelection;
    };

    public getIsLoadingItems = (): ObservableValue<boolean> => {
        return this.isLoadingItems;
    };

    public getIsLoadingMembers = (): ObservableValue<boolean> => {
        return this.isLoadingMembers;
    };

    public getIdentityImageUrl = (teamId: string): string => {
        return getIdentityImageUrl(this.pageContext, teamId);
    };

    public getItemProvider = (teamId: string, teamName: string): IObservableArray<ITeamPanelItemView> => {
        this.getAllItems(teamId, teamName);
        return this.filteredItems;
    };

    public getMemberProvider(teamId: string): IObservableArray<TeamMember> {
        this.getAllMembers(teamId);
        return this.members;
    }

    public getSettingsUrl = (teamId: string): string => {
        const routeValues: { [key: string]: string } = {};
        const project = this.getTeamProject();

        if (project) {
            routeValues["adminPivot"] = Types.Teams;
            routeValues["project"] = project.name;
            routeValues["teamId"] = teamId;

            return routeUrl(this.pageContext, "ms.vss-admin-web.project-admin-hub-route", routeValues);
        }

        return "";
    };

    public getTeamProject = (): IProjectData | undefined => {
        const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
        const pageServiceData = tfsPageService.getData();

        return pageServiceData && pageServiceData.project;
    };

    private convertToTeamPanelItemView = (panelItems: ITeamPanelItem[], hub: string): ITeamPanelItemView[] => {
        this.dropdownItems.push(hub);

        const iconName = getIconNameByHub(hub);

        return panelItems.map((value: ITeamPanelItem) => ({
            item: {
                ...value,
                iconClassName: iconName
            },
            hub
        }));
    };

    private getAllItems = (teamId: string, teamName: string) => {
        if (this.teamId !== teamId || this.teamName !== teamName) {
            this.isLoadingItems.value = true;
            this.teamId = teamId;
            this.teamName = teamName;
            this.dropdownItems.value = [Resources.AllItems];
            this.hubSelection.select(0);

            this.loadTeamProfilesData(teamId, teamName).then((results: ITeamPanelItemView[]) => {
                this.isLoadingItems.value = false;
                this.allItems = results;
                this.filterAllItems();
            });
        }
    };

    private getAllMembers = (teamId: string) => {
        if (this.teamId !== teamId || !this.membersPromise) {
            this.isLoadingMembers.value = true;
            this.teamId = teamId;

            const projectId = this.getTeamProject()?.id;
            if (projectId) {
                const coreRestClient: ICoreRestClient = this.pageContext.getRestClient("ICoreRestClient");

                this.membersPromise = coreRestClient
                    .getTeamMembersWithExtendedProperties(projectId, teamId, this.maxMembers)
                    .then((result: TeamMember[]) => {
                        this.isLoadingMembers.value = false;
                        result.sort(alphabeticalAdminComparer());
                        this.members.value = result;
                    });
            }
        }
    };

    private filterAllItems = () => {
        let hub = Resources.AllItems;
        const selection = this.hubSelection.value;
        if (selection.length > 0) {
            hub = this.dropdownItems.value[selection[0].beginIndex];
        }

        this.filteredItems.value = this.filterItemsByHub(this.allItems, hub);
    };

    private filterItemsByHub = (allItems: ITeamPanelItemView[], hub: string) => {
        if (hub === Resources.AllItems) {
            return allItems.filter((panelItem: ITeamPanelItemView) => panelItem && panelItem.item);
        }

        return allItems.filter((panelItem: ITeamPanelItemView) => panelItem && panelItem.item && panelItem.hub === hub);
    };

    private loadTeamProfilesData = (teamId: string, teamName: string) => {
        const project = this.getTeamProject();

        if (!project?.id || !project?.name) {
            return Promise.resolve([]);
        }

        const params: ITeamPanelContext = {
            projectId: project.id,
            projectName: project.name,
            teamId,
            teamName
        };

        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");

        return Promise.all([
            contributionService
                .getDataAsync<ITeamPanelDataProviderResponse>(teamPanelDataProviders.teamPanelItemsBoardsDataProvider, params)
                .then((results: ITeamPanelDataProviderResponse) => {
                    if (!results.error && results.items.length > 0) {
                        return this.convertToTeamPanelItemView(results.items, Resources.Boards);
                    }
                }),
            contributionService
                .getDataAsync<ITeamPanelDataProviderResponse>(teamPanelDataProviders.teamPanelItemsBacklogsDataProvider, params)
                .then((results: ITeamPanelDataProviderResponse) => {
                    if (!results.error && results.items.length > 0) {
                        return this.convertToTeamPanelItemView(results.items, Resources.Backlogs);
                    }
                }),
            contributionService
                .getDataAsync<ITeamPanelDataProviderResponse>(teamPanelDataProviders.teamPanelItemsSprintsDataProvider, params)
                .then((results: ITeamPanelDataProviderResponse) => {
                    if (!results.error && results.items.length > 0) {
                        return this.convertToTeamPanelItemView(results.items, Resources.Sprints);
                    }
                }),
            contributionService
                .getDataAsync<ITeamPanelDataProviderResponse>(teamPanelDataProviders.teamPanelItemsQueriesDataProvider, params)
                .then((results: ITeamPanelDataProviderResponse) => {
                    if (!results.error && results.items.length > 0) {
                        return this.convertToTeamPanelItemView(results.items, Resources.Queries);
                    }
                }),
            contributionService
                .getDataAsync<ITeamPanelDataProviderResponse>(teamPanelDataProviders.teamPanelItemsDashboardsDataProvider, params)
                .then((results: ITeamPanelDataProviderResponse) => {
                    if (!results.error && results.items.length > 0) {
                        return this.convertToTeamPanelItemView(results.items, Resources.Dashboards);
                    }
                })
        ]).then((responses) => responses.reduce((prev, curr) => (curr ? prev?.concat(curr) : prev), []));
    };
}

// Admins on top in ABC order, followed by all other members in ABC order
function alphabeticalAdminComparer(): ((a: TeamMember, b: TeamMember) => number) | undefined {
    return (a: TeamMember, b: TeamMember) => {
        if (a.isTeamAdmin === b.isTeamAdmin) {
            return a.identity.displayName.localeCompare(b.identity.displayName, navigator.language);
        } else if (a.isTeamAdmin) {
            return -1;
        } else {
            return 1;
        }
    };
}

function getIconNameByHub(hub: string) {
    let iconName = "";

    switch (hub) {
        case Types.Backlogs:
            iconName = "BacklogList";
            break;
        case Types.Boards:
            iconName = "BacklogBoard";
            break;
        case Types.Dashboards:
            iconName = "ViewDashboard";
            break;
        case Types.Sprints:
            iconName = "Sprint";
            break;
        case Types.Queries:
            iconName = "QueryList";
            break;
    }
    return iconName;
}

Services.add("ITeamProfilePanelService", { serviceFactory: TeamProfilePanelService });
